import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  UserCircleIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import ReactPaginate from 'react-paginate';
import '../../Pagination.css';

import {
  useGetUserEmployeesPageChangeMutation,
  useGetUserEmployeesQuery,
  useGetUserEmployeesSearchMutation
} from '../../Features/userEmployeesSlice';

import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useTheme } from '../../ThemeContext';

function AllUserEmployeesView({ props }) {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { isOnline } = useIsOnline();

  const [search, setSearch] = useState(0);
  const [pager, setPager] = useState("");
  const [progress, setProgress] = useState(0);
  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const user = useSelector(selectCurrentUser);

  const {
    data: users,
    isLoading,
    isSuccess,
    isError,
    error
  } = useGetUserEmployeesQuery({
    account_id: user.id,
    paginate: numRowsPerPage
  });

  const [input, setInput] = useState({
    search: '',
    rowsPerPage: ''
  });

  const handleChange = (e) => {
    setInput({
      ...input,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && users?.data) {
      setCurrentItems(users.data.data || []);
      setPageCount(
        Math.ceil((users.data.total || 0) / numRowsPerPage)
      );
      setProgress(100);
    }

    if (isError) {
      setProgress(100);
    }

    return () => {};
  }, [users, isLoading, isSuccess, isError, numRowsPerPage]);

  const [pageChange] = useGetUserEmployeesPageChangeMutation();

  const onChangePageRows = async (rows) => {
    const rowsPerPage = window.parseInt(rows);
    const searchValue = input.search ? input.search : 0;

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await pageChange({
        account_id: user.id,
        paginate: rowsPerPage,
        search: searchValue,
        page: 1
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(resp.data.total / rowsPerPage)
        );

        setNumRowsPerPage(rowsPerPage);
        setPager(1);
        setProgress(100);
      }

    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message || "Something went wrong",
        "error"
      );
    }
  };

  const handlePageChange = async (currentPage) => {
    const pageNum = window.parseInt(currentPage) + 1;
    const searchValue = input.search ? input.search : 0;

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await pageChange({
        account_id: user.id,
        paginate: numRowsPerPage,
        search: searchValue,
        page: pageNum
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(resp.data.total / numRowsPerPage)
        );

        setPager(pageNum);
        setProgress(100);
      }

    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message || "Something went wrong",
        "error"
      );
    }
  };

  const [searchData] = useGetUserEmployeesSearchMutation();

  const submitSearch = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await searchData({
        account_id: user.id,
        numRowsPerPage,
        paginate: numRowsPerPage,
        search: input.search,
        page: 1
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(resp.data.total / numRowsPerPage)
        );

        setPager(1);
        setProgress(100);
      }

    } catch (err) {
      setInput({
        ...input,
        search: ''
      });

      setProgress(100);

      swal('No results found...');
    }
  };

  const UserProfile = (e, id) => {
    if (isOnline) {
      navigate("/client/edit-employee/" + id);
    } else {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again",
        "error"
      );
    }
  };

  const truncateText = (str) => {
    if (!str) return "";

    return str.length > 25
      ? str.substring(0, 25) + "..."
      : str;
  };

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

        {/* PAGE HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">

          <div className="flex items-center gap-3">

            <div className="w-11 h-11 rounded-xl
                            bg-[#80b643]/10 dark:bg-[#80b643]/20
                            flex items-center justify-center">

              <UserCircleIcon className="w-6 h-6 text-[#80b643]" />

            </div>

            <div>

              <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                Employees
              </h1>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your employees and their account details
              </p>

            </div>

          </div>

          <Link
            to="/client/create-employee"
            className="inline-flex items-center justify-center gap-2
                       px-4 py-2.5 rounded-xl
                       bg-[#80b643] hover:bg-[#719f3a]
                       text-white text-sm font-medium
                       shadow-sm transition-all duration-200
                       w-full sm:w-auto"
          >

            <PlusIcon className="w-5 h-5" />

            <span>Add Employee</span>

          </Link>

        </div>

        {/* MAIN CARD */}
        <div className="bg-white dark:bg-gray-900
                        border border-gray-200 dark:border-gray-800
                        rounded-xl shadow-sm overflow-hidden">

          {/* CARD HEADER */}
          <div className="px-4 sm:px-6 py-4
                          border-b border-gray-200 dark:border-gray-800">

            <div className="flex flex-col md:flex-row
                            md:items-center md:justify-between gap-4">

              <div>

                <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
                  Employee List
                </h2>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  View and manage employees registered under your account
                </p>

              </div>

              {/* SEARCH */}
              <form
                onSubmit={submitSearch}
                className="w-full md:w-72"
              >

                <div className="relative">

                  <SearchIcon
                    className="w-4 h-4 absolute left-3 top-1/2
                               -translate-y-1/2
                               text-gray-400
                               pointer-events-none"
                  />

                  <input
                    type="text"
                    name="search"
                    value={input.search}
                    onChange={handleChange}
                    placeholder="Search employees..."
                    autoComplete="off"
                    aria-label="Search employee"
                    className="w-full
                               pl-9 pr-4 py-2.5
                               text-sm
                               text-gray-700 dark:text-white
                               placeholder-gray-400 dark:placeholder-gray-500
                               bg-gray-50 dark:bg-gray-800
                               border border-gray-200 dark:border-gray-700
                               rounded-lg
                               outline-none
                               focus:border-[#80b643]
                               focus:ring-1 focus:ring-[#80b643]
                               transition-all duration-200"
                  />

                </div>

              </form>

            </div>

          </div>

          {/* TABLE */}
          {currentItems.length > 0 ? (

            <div className="overflow-x-auto">

              <table className="w-full min-w-[750px]">

                <thead className="bg-gray-50 dark:bg-gray-800/70
                                  border-b border-gray-200 dark:border-gray-800">

                  <tr>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-left
                                 text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400
                                 uppercase tracking-wider"
                    >
                      Name
                    </th>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-left
                                 text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400
                                 uppercase tracking-wider"
                    >
                      Email
                    </th>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-left
                                 text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400
                                 uppercase tracking-wider"
                    >
                      Phone
                    </th>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-left
                                 text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400
                                 uppercase tracking-wider"
                    >
                      Role
                    </th>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-right
                                 text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400
                                 uppercase tracking-wider"
                    >
                      Action
                    </th>

                  </tr>

                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                  {currentItems.map((data) => (

                    <tr
                      onClick={(e) => UserProfile(e, data.id)}
                      key={data.id}
                      className="group
                                 hover:bg-gray-50 dark:hover:bg-gray-800/60
                                 cursor-pointer
                                 transition-colors duration-150"
                    >

                      {/* NAME */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="flex items-center gap-3">

                          <div className="w-9 h-9 rounded-lg
                                          bg-[#80b643]/10 dark:bg-[#80b643]/20
                                          flex items-center justify-center
                                          flex-shrink-0">

                            <span className="text-sm font-semibold text-[#80b643]">
                              {data.fname
                                ? data.fname.charAt(0).toUpperCase()
                                : "U"}
                            </span>

                          </div>

                          <div className="min-w-0">

                            <div className="text-sm font-medium
                                            text-gray-800 dark:text-white
                                            truncate">

                              {truncateText(
                                data.lname + ' ' + data.fname
                              )}

                            </div>

                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              Employee
                            </div>

                          </div>

                        </div>

                      </td>

                      {/* EMAIL */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[220px]">
                          {truncateText(data.email)}
                        </div>

                      </td>

                      {/* PHONE */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {truncateText(data.phone)}
                        </div>

                      </td>

                      {/* ROLE */}
                      <td className="px-4 sm:px-6 py-4">

                        <span className="inline-flex items-center
                                         px-2.5 py-1 rounded-lg
                                         bg-[#80b643]/10
                                         dark:bg-[#80b643]/20
                                         text-[#80b643]
                                         text-xs font-medium">

                          {data.role}

                        </span>

                      </td>

                      {/* ACTION */}
                      <td className="px-4 sm:px-6 py-4 text-right">

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            UserProfile(e, data.id);
                          }}
                          className="inline-flex items-center justify-center gap-1.5
                                     px-3 py-1.5 rounded-lg
                                     border border-gray-200 dark:border-gray-700
                                     text-xs font-medium
                                     text-gray-600 dark:text-gray-300
                                     hover:text-[#80b643]
                                     hover:border-[#80b643]
                                     hover:bg-[#80b643]/5
                                     transition-all duration-200"
                        >

                          <PencilIcon className="w-3.5 h-3.5" />

                          <span className="hidden sm:inline">
                            Edit
                          </span>

                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          ) : (

            /* EMPTY STATE */
            <div className="flex flex-col items-center justify-center
                            px-6 py-16 text-center">

              <div className="w-16 h-16 rounded-xl
                              bg-gray-100 dark:bg-gray-800
                              flex items-center justify-center mb-4">

                <UserCircleIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm sm:text-base font-semibold
                             text-gray-700 dark:text-gray-200">

                No employees found

              </h3>

              <p className="text-xs sm:text-sm
                            text-gray-500 dark:text-gray-400
                            mt-1 max-w-sm">

                There are currently no employees to display.
                Add an employee to get started.

              </p>

              <Link
                to="/client/create-employee"
                className="inline-flex items-center justify-center gap-2
                           mt-5 px-4 py-2.5 rounded-xl
                           bg-[#80b643] hover:bg-[#719f3a]
                           text-white text-sm font-medium
                           shadow-sm transition-all duration-200"
              >

                <PlusIcon className="w-5 h-5" />

                Add Employee

              </Link>

            </div>

          )}

          {/* PAGINATION */}
          {currentItems.length > 0 && (

            <div className="flex flex-col sm:flex-row
                            sm:items-center sm:justify-between
                            gap-4 px-4 sm:px-6 py-4
                            border-t border-gray-200 dark:border-gray-800">

              {/* PAGINATION */}
              <ReactPaginate
                breakLabel="..."
                nextLabel={
                  <ChevronRightIcon
                    className="h-8 w-8
                               text-gray-400 dark:text-white
                               bg-white dark:bg-gray-800
                               cursor-pointer
                               hover:bg-gray-50 dark:hover:bg-gray-700"
                  />
                }
                onPageChange={(e) =>
                  handlePageChange(e.selected)
                }
                pageRangeDisplayed={3}
                pageCount={pageCount}
                previousLabel={
                  <ChevronLeftIcon
                    className="h-8 w-8
                               text-gray-400 dark:text-white
                               bg-white dark:bg-gray-800
                               cursor-pointer
                               hover:bg-gray-50 dark:hover:bg-gray-700"
                  />
                }
                renderOnZeroPageCount={null}
                containerClassName="pagination"
                pageLinkClassName="page-num"
                previousLinkClassName="controlBtn"
                nextLinkClassName="controlBtn"
                activeLinkClassName={`${
                  darkMode ? '' : 'active'
                } dark:text-white
                   bg-white dark:bg-gray-700
                   hover:dark:text-white
                   hover:dark:bg-gray-600`}
              />

              {/* ROWS PER PAGE */}
              <div className="flex items-center
                              justify-between sm:justify-end gap-2">

                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Rows per page
                </span>

                <select
                  value={numRowsPerPage}
                  onChange={(e) =>
                    onChangePageRows(e.target.value)
                  }
                  className="w-20 px-2 py-1.5
                             text-xs
                             text-gray-600 dark:text-gray-300
                             bg-white dark:bg-gray-800
                             border border-gray-200 dark:border-gray-700
                             rounded-lg
                             outline-none
                             focus:border-[#80b643]
                             focus:ring-1 focus:ring-[#80b643]
                             cursor-pointer"
                >

                  <option
                    value="10"
                    className="text-gray-800 dark:text-white"
                  >
                    10
                  </option>

                  <option
                    value="25"
                    className="text-gray-800 dark:text-white"
                  >
                    25
                  </option>

                  <option
                    value="50"
                    className="text-gray-800 dark:text-white"
                  >
                    50
                  </option>

                  <option
                    value="100"
                    className="text-gray-800 dark:text-white"
                  >
                    100
                  </option>

                </select>

              </div>

            </div>

          )}

        </div>

      </div>

    </div>
  );
}

export default AllUserEmployeesView;